import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'

import { ToastContext, type Toast, type ToastTone } from './toast-context'

const DISMISS_AFTER_MS = 4200

const toneClasses: Record<ToastTone, string> = {
  neutral: 'border-[var(--hairline-strong)]',
  positive: 'border-verderame-500/60',
  critical: 'border-ruggine-400/70',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const counter = useRef(0)

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const notify = useCallback(
    (message: string, tone: ToastTone = 'neutral') => {
      counter.current += 1
      const id = `toast-${counter.current}`
      setToasts((current) => [...current, { id, message, tone }])
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DISMISS_AFTER_MS),
      )
      return id
    },
    [dismiss],
  )

  const value = useMemo(() => ({ notify, dismiss }), [notify, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Regione annunciata: i messaggi arrivano anche a chi non guarda lo schermo. */}
      <div
        role="status"
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed top-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'bg-ink-700 pointer-events-auto flex items-start gap-3 rounded-lg border p-3',
              'text-linen-200 text-sm shadow-[var(--shadow-lift)]',
              toneClasses[toast.tone],
            )}
          >
            <p className="flex-1">{toast.message}</p>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Chiudi la notifica"
              className="text-thread-400 hover:text-linen-100 -m-1 shrink-0 rounded-sm p-1 leading-none"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
